import { useState, useEffect } from 'react';
import { accountabilityAPI } from '../lib/api';
import { Shield, Download, AlertTriangle, Gavel, Clock, CheckCircle2, XCircle, FileText, Loader2 } from 'lucide-react';

const STRIKE_LEVEL_COPY = {
  1: { label: 'STRIKE 1 · Warning', color: '#D4A84A', bg: 'rgba(212,168,74,0.08)' },
  2: { label: 'STRIKE 2 · 30-day restriction', color: '#FF7A00', bg: 'rgba(255,122,0,0.08)' },
  3: { label: 'STRIKE 3 · Permanent suspension', color: '#FF4444', bg: 'rgba(255,68,68,0.08)' },
};

const TRIGGER_COPY = {
  ai_auto: 'AI moderation (high severity)',
  admin_manual: 'Admin review',
  teacher_justification_pattern: 'Pattern: unjustified Fail verdicts',
};

function StrikeCard({ strike, onFileAppeal, pendingAppeal }) {
  const copy = STRIKE_LEVEL_COPY[strike.strike_level] || STRIKE_LEVEL_COPY[1];
  const isExpunged = strike.expunged;
  const statusChip = isExpunged
    ? { label: 'EXPUNGED', color: '#52525B' }
    : pendingAppeal
    ? { label: 'APPEAL PENDING', color: '#2F8A92' }
    : { label: 'ACTIVE', color: copy.color };

  return (
    <div
      className="border rounded p-4 space-y-2"
      style={{ borderColor: `${copy.color}33`, background: isExpunged ? 'rgba(82,82,91,0.04)' : copy.bg }}
      data-testid={`strike-card-${strike.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-hud text-xs tracking-widest uppercase" style={{ color: isExpunged ? '#52525B' : copy.color }}>
          {copy.label}
        </p>
        <span
          className="font-hud text-[9px] tracking-wider px-2 py-0.5 rounded border"
          style={{ borderColor: `${statusChip.color}66`, color: statusChip.color }}
        >
          {statusChip.label}
        </span>
      </div>
      <p className="font-mono text-[11px] text-white/80">{strike.reason}</p>
      <p className="font-mono text-[9px] text-[#52525B]">
        Issued {new Date(strike.issued_at).toLocaleString()} · Trigger: {TRIGGER_COPY[strike.trigger] || strike.trigger}
        {strike.expunged && (<> · Expunged {new Date(strike.expunged_at).toLocaleDateString()}</>)}
      </p>
      {!isExpunged && !pendingAppeal && (
        <button
          data-testid={`appeal-btn-${strike.id}`}
          onClick={() => onFileAppeal(strike)}
          className="font-hud text-[10px] tracking-wider text-[#2F8A92] border border-[#2F8A92]/30 px-3 py-1 hover:bg-[#2F8A92]/10 transition-all flex items-center gap-1"
        >
          <Gavel size={10} /> FILE APPEAL
        </button>
      )}
    </div>
  );
}

function AppealForm({ strike, onFiled, onCancel }) {
  const [statement, setStatement] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handle = async () => {
    if (statement.trim().length < 20) {
      setError('Statement must be at least 20 characters.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const r = await accountabilityAPI.fileAppeal(strike.id, statement.trim());
      onFiled(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to file appeal.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#2F8A92]/20 rounded p-4 space-y-2" data-testid="appeal-form">
      <h4 className="font-hud text-sm tracking-widest uppercase text-[#2F8A92] flex items-center gap-2">
        <Gavel size={14} /> APPEAL STRIKE {strike.strike_level}
      </h4>
      <p className="font-mono text-[10px] text-[#52525B]">
        {strike.strike_level === 3
          ? 'Strike 3 appeals go directly to admin (14-day SLA).'
          : `Strike ${strike.strike_level} appeals reviewed by Founder Teacher panel (${strike.strike_level === 1 ? '7' : '5'}-day SLA). Admin can override at any time.`}
      </p>
      <textarea
        data-testid="appeal-statement-input"
        className="hud-input text-xs min-h-[120px] resize-y"
        placeholder="Your statement — up to 500 characters. Explain what happened, what you think was wrong, and what should change."
        value={statement}
        onChange={(e) => setStatement(e.target.value.slice(0, 500))}
      />
      <div className="flex items-center justify-between">
        <span className="font-mono text-[9px] text-[#52525B]">{statement.length} / 500</span>
        {error && <span className="font-mono text-[10px] text-red-400">{error}</span>}
      </div>
      <div className="flex gap-2">
        <button
          data-testid="submit-appeal-btn"
          onClick={handle}
          disabled={submitting}
          className="font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/80 disabled:opacity-40 flex items-center gap-1"
        >
          {submitting ? <Loader2 size={10} className="animate-spin" /> : <Gavel size={10} />}
          SUBMIT APPEAL
        </button>
        <button onClick={onCancel} className="font-hud text-xs text-white/80 tracking-wider hover:text-[#2F8A92]">CANCEL</button>
      </div>
    </div>
  );
}

function AuditTimeline({ events }) {
  if (!events?.length) {
    return <p className="font-mono text-[10px] text-[#52525B]">No events yet.</p>;
  }
  return (
    <div className="space-y-2" data-testid="audit-timeline">
      {events.map((e) => (
        <div key={e.id} className="border-l-2 border-[#2F8A92]/20 pl-3 py-1">
          <p className="font-hud text-[10px] tracking-wider text-[#2F8A92] uppercase">{e.event_type.replace(/_/g, ' ')}</p>
          <p className="font-mono text-[9px] text-[#52525B]">{new Date(e.created_at).toLocaleString()}</p>
          {e.context?.verdict && (
            <p className="font-mono text-[10px] text-white/80">Verdict: <span style={{ color: e.context.verdict === 'pass' ? '#2F8A92' : '#FF7A00' }}>{e.context.verdict.toUpperCase()}</span></p>
          )}
          {e.context?.strike && (
            <p className="font-mono text-[10px] text-white/80">Strike level {e.context.strike.strike_level} · {e.context.strike.reason?.slice(0, 120)}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AccountabilityPage() {
  const [strikes, setStrikes] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [audit, setAudit] = useState([]);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appealingStrike, setAppealingStrike] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      accountabilityAPI.myStrikes(),
      accountabilityAPI.myAppeals(),
      accountabilityAPI.myAudit(),
    ])
      .then(([s, a, au]) => {
        setStrikes(s.data.strikes || []);
        setState(s.data.state);
        setAppeals(a.data || []);
        setAudit(au.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await accountabilityAPI.exportMyData();
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tsogn-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="font-hud text-[#2F8A92] text-sm tracking-wider animate-pulse p-6">LOADING ACCOUNT RECORD…</div>;
  }

  const pendingAppealsByStrike = new Map();
  for (const a of appeals) {
    if (a.status === 'pending') pendingAppealsByStrike.set(a.strike_id, a);
  }

  return (
    <div className="p-6 space-y-5 max-w-3xl" data-testid="accountability-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-historical text-[#D4A84A] text-2xl md:text-3xl lg:text-4xl font-light tracking-tight">My Record</h2>
          <p className="font-hud text-[10px] md:text-xs text-white/80 tracking-wider mt-1">
            STRIKES · APPEALS · AUDIT TRAIL · DATA EXPORT
          </p>
        </div>
        <button
          data-testid="export-data-btn"
          onClick={handleExport}
          disabled={exporting}
          className="font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-3 py-2 hover:bg-[#66FAFF] disabled:opacity-40 transition-all flex items-center gap-2"
        >
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          EXPORT MY DATA
        </button>
      </div>

      <p className="font-mono text-[10px] text-[#52525B] italic">
        "Your data stays yours." — TSOGN methodology
      </p>

      {state && (
        <div
          className="border rounded p-3"
          style={{
            borderColor: state.permanent_suspension
              ? '#FF444444'
              : state.in_soft_mode
              ? '#FF7A0044'
              : '#2F8A9233',
          }}
          data-testid="account-state-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <Shield
              size={12}
              className=""
              style={{
                color: state.permanent_suspension
                  ? '#FF4444'
                  : state.in_soft_mode
                  ? '#FF7A00'
                  : '#2F8A92',
              }}
            />
            <span className="font-hud text-[10px] tracking-widest uppercase" style={{
              color: state.permanent_suspension ? '#FF4444' : state.in_soft_mode ? '#FF7A00' : '#2F8A92',
            }}>
              {state.permanent_suspension
                ? 'PERMANENTLY SUSPENDED'
                : state.in_soft_mode
                ? 'SOFT MODE — LIMITED ACCESS'
                : 'ACCOUNT IN GOOD STANDING'}
            </span>
          </div>
          <p className="font-mono text-[10px] text-white/80">
            {state.active_strikes} active strike{state.active_strikes === 1 ? '' : 's'}
            {state.soft_mode_until && (<> · lifts {new Date(state.soft_mode_until).toLocaleString()}</>)}
          </p>
          {state.in_soft_mode && (
            <p className="font-mono text-[10px] text-[#52525B] mt-1">
              You can still read, work in your Notepad, and export your data. Publishing, inviting, and commenting resume when the window lifts.
            </p>
          )}
        </div>
      )}

      {appealingStrike && (
        <AppealForm
          strike={appealingStrike}
          onFiled={() => { setAppealingStrike(null); load(); }}
          onCancel={() => setAppealingStrike(null)}
        />
      )}

      <div>
        <h3 className="font-hud text-sm tracking-widest uppercase text-[#2F8A92] mb-2 flex items-center gap-2">
          <AlertTriangle size={14} /> STRIKES ({strikes.length})
        </h3>
        {strikes.length === 0 ? (
          <p className="font-mono text-[10px] text-[#52525B]">No strikes on record. Stay curious.</p>
        ) : (
          <div className="space-y-2">
            {strikes.map((s) => (
              <StrikeCard
                key={s.id}
                strike={s}
                pendingAppeal={pendingAppealsByStrike.get(s.id)}
                onFileAppeal={setAppealingStrike}
              />
            ))}
          </div>
        )}
      </div>

      {appeals.length > 0 && (
        <div>
          <h3 className="font-hud text-sm tracking-widest uppercase text-[#2F8A92] mb-2 flex items-center gap-2">
            <Gavel size={14} /> APPEALS ({appeals.length})
          </h3>
          <div className="space-y-2">
            {appeals.map((a) => (
              <div key={a.id} className="border border-[#2F8A92]/10 rounded p-3" data-testid={`my-appeal-${a.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-hud text-[10px] tracking-wider uppercase text-[#2F8A92]">
                    STRIKE {a.strike_level} · {a.route.replace('_', ' ')}
                  </span>
                  <span
                    className="font-hud text-[9px] tracking-wider px-2 py-0.5 rounded border flex items-center gap-1"
                    style={{
                      borderColor: a.status === 'pending' ? '#D4A84A66' : '#52525B66',
                      color: a.status === 'pending' ? '#D4A84A' : '#A1A1AA',
                    }}
                  >
                    {a.status === 'pending' && <Clock size={9} />}
                    {a.status === 'decided' && a.decision === 'expunge' && <CheckCircle2 size={9} />}
                    {a.status === 'decided' && a.decision === 'uphold' && <XCircle size={9} />}
                    {a.status.toUpperCase()}{a.decision && ` · ${a.decision.toUpperCase()}`}
                  </span>
                </div>
                <p className="font-mono text-[11px] text-white/80 mt-2">{a.statement}</p>
                <p className="font-mono text-[9px] text-[#52525B] mt-1">
                  Filed {new Date(a.created_at).toLocaleString()} · SLA {a.sla_days} days
                </p>
                {a.admin_override && (
                  <p className="font-mono text-[10px] text-[#D4A84A] mt-1">
                    ADMIN OVERRIDE · {a.admin_override.reason}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-hud text-sm tracking-widest uppercase text-[#2F8A92] mb-2 flex items-center gap-2">
          <FileText size={14} /> AUDIT TRAIL ({audit.length})
        </h3>
        <p className="font-mono text-[10px] text-[#52525B] mb-2">
          Every platform decision about your account, chronologically. You own this record.
        </p>
        <AuditTimeline events={audit} />
      </div>
    </div>
  );
}
